import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { HourlyPackage } from './HourlyPackage';
import type { ContractFormValues } from '../schemas';

interface PricingSectionProps {
  isFieldDisabled: boolean;
  isEditMode: boolean;
  selectedType: string;
  selectedCurrency: string;
}

export function PricingSection({
  isFieldDisabled,
  isEditMode,
  selectedType,
  selectedCurrency,
}: PricingSectionProps) {
  const { t } = useTranslation();
  const form = useFormContext<ContractFormValues>();

  return (
    <div className="space-y-4">
      {/* SUCCESS_FEE fields */}
      {selectedType === 'SUCCESS_FEE' && (
        <>
          <FormField
            name="claimedPrice"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('claimedPrice')} {selectedCurrency === 'USD' ? '$' : 'UZS'}
                </FormLabel>
                <FormControl>
                  <NumericFormat
                    thousandSeparator=" "
                    allowNegative={false}
                    decimalScale={2}
                    customInput={Input}
                    disabled={isFieldDisabled}
                    placeholder={t('pricePlaceholder')}
                    className="bg-white"
                    value={field.value ?? ''}
                    onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 space-y-4">
            <h4 className="text-sm font-medium text-slate-700">{t('successPackageDetailsOptional')}</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                name="successPackage.advancePayment"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('advancePayment')} {selectedCurrency === 'USD' ? '$' : 'UZS'}
                    </FormLabel>
                    <FormControl>
                      <NumericFormat
                        thousandSeparator=" "
                        allowNegative={false}
                        decimalScale={2}
                        customInput={Input}
                        placeholder="0"
                        className="bg-white"
                        value={field.value ?? ''}
                        onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                name="successPackage.successPayment"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('successPayment')} {selectedCurrency === 'USD' ? '$' : 'UZS'}
                    </FormLabel>
                    <FormControl>
                      <NumericFormat
                        thousandSeparator=" "
                        allowNegative={false}
                        decimalScale={2}
                        customInput={Input}
                        placeholder="0"
                        className="bg-white"
                        value={field.value ?? ''}
                        onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </>
      )}

      {/* HOURLY fields */}
      {selectedType === 'HOURLY' && (
        <>
          <FormField
            name="hourlyPackageId"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('startFromTemplate')}</FormLabel>
                <FormControl>
                  <UniversalCombobox
                    url="/Hourly-Packages"
                    labelKeys={['name', 'hours', 'price']}
                    valueKey="id"
                    params={{ status: 'ACTIVE' }}
                    selectedId={field.value}
                    disabled={isFieldDisabled}
                    // In edit mode this field is preselected with the contract's
                    // OWN (cloned) package, and resolving it echoed back through
                    // onSelectItem — re-running the template prefill on every
                    // page load: it re-converted the tariff by dolarRate and
                    // replaced `rates` with the package's stored rows, wiping
                    // rates the user had just typed for roles the tariff lacks.
                    // The prefill must only run on a real user pick.
                    notifyOnPreselect={false}
                    onSelectItem={(item) => {
                      field.onChange(item?.['id'] ?? null);
                      if (item) {
                        const itemWithRates = item as {
                          id?: number;
                          curency?: 'USD' | 'UZS';
                          hours?: number;
                          price?: number;
                          overTimeHourlyCost?: number;
                          rates?: { roleId: number; rate?: number }[];
                        };

                        // The template may be in a different currency than the
                        // contract — convert its money fields into the contract's
                        // currency using the contract's dolarRate (USD→UZS ×rate,
                        // UZS→USD ÷rate). Hours are not money.
                        const from = itemWithRates.curency ?? 'USD';
                        const dolarRate = form.getValues('dolarRate');
                        const conv = (v?: number) => {
                          if (v == null) return v;
                          if (from === selectedCurrency || !dolarRate) return v;
                          if (selectedCurrency === 'UZS' && from === 'USD') return Math.round(v * dolarRate);
                          if (selectedCurrency === 'USD' && from === 'UZS') return Math.round(v / dolarRate);
                          return v;
                        };

                        const price = conv(itemWithRates.price);
                        form.setValue('hourlyPackage.hours', itemWithRates.hours, { shouldValidate: true });
                        form.setValue('hourlyPackage.price', price, { shouldValidate: true });
                        form.setValue('hourlyPackage.overTimeHourlyCost', conv(itemWithRates.overTimeHourlyCost), { shouldValidate: true });
                        form.setValue(
                          'hourlyPackage.rates',
                          (itemWithRates.rates ?? []).map((r) => ({ roleId: r.roleId, rate: conv(r.rate) })),
                          { shouldValidate: true }
                        );

                        form.setValue('minAdvanceForHourly', price, { shouldValidate: true });
                        if (price && !isEditMode) {
                          form.setValue('advancePayment', price, { shouldValidate: true });
                        }
                      } else {
                        form.setValue('hourlyPackage.rates', [], { shouldValidate: true });
                      }
                    }}
                    placeholder={t('selectHourlyPackage')}
                    className="bg-white"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
            <h4 className="text-sm font-medium text-slate-700 mb-4">{t('hourlyTariff')}</h4>
            <HourlyPackage form={form} />
          </div>
        </>
      )}

      {/* FIXED fields */}
      {selectedType === 'FIXED' && (
        <FormField
          name="price"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('price')} {selectedCurrency === 'USD' ? '$' : 'UZS'} *
              </FormLabel>
              <FormControl>
                <NumericFormat
                  thousandSeparator=" "
                  allowNegative={false}
                  decimalScale={0}
                  customInput={Input}
                  disabled={isFieldDisabled}
                  placeholder={t('pricePlaceholder')}
                  className="bg-white"
                  value={field.value ?? ''}
                  onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
